import React, { useState } from "react"
import { Eye, Calendar, ShieldCheck, Sparkles, PlayCircle, Radio, Film, TrendingUp, Users, Heart, MessageCircle, Share2, BadgeCheck, Percent, ExternalLink, Music } from "lucide-react"
import { CONTENT_TYPE_META, SENTIMENT_META } from "../constants"
import { formatViews } from "../utils"
import Thumbnail from "./Thumbnail"
import Avatar from "./Avatar"
import { useAnalytics, ANALYTICS_EVENTS } from "../../../analytics"

// Reach-based tiers (YouTube/Instagram) - "observed reach, median views
// across the store", never a real follower count for either platform.
const CREATOR_TIER_LABEL = {
  micro: "Micro creator",
  mid: "Mid-size creator",
  major: "Major creator",
  mega: "Mega creator",
  unknown: "Unrated creator",
}

// TikTok's tiers are real follower bands, not reach (TIKTOK_VIDEO_API.md §1/§2:
// "real follower bands, `authorFollowers` stored per video") - six tiers, with
// `nano` at the bottom.
const CREATOR_TIER_LABEL_TIKTOK = {
  nano: "Nano creator",
  micro: "Micro creator",
  mid: "Mid-size creator",
  macro: "Macro creator",
  mega: "Mega creator",
  unknown: "Unrated creator",
}

const Chip = ({ label, color }) => (
  <span
    style={{
      background: `${color}18`,
      color,
      fontSize: "11px",
      fontWeight: 600,
      padding: "3px 9px",
      borderRadius: "6px",
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </span>
)

/**
 * One verified UGC video. Clicking anywhere on the card swaps the grid for
 * the full detail view (VideoDetailSection); the channel link stops
 * propagation so it navigates away instead of also opening the detail view.
 */
const VideoCard = ({ video, onOpen }) => {
  const { trackEvent } = useAnalytics()
  const [hovered, setHovered] = useState(false)
  const isInstagram = video.platform === "instagram"
  const isTikTok = video.platform === "tiktok"
  // Both platforms' source video is vertical (9:16) - see the matching
  // `.ugc-video-grid--reels` narrower columns in UGCExplorer.jsx.
  const isPortrait = isInstagram || isTikTok
  const contentType = CONTENT_TYPE_META[video.summary?.content_type] || CONTENT_TYPE_META.unknown
  const sentiment = SENTIMENT_META[video.summary?.sentiment] || SENTIMENT_META.unknown
  const isMetaBased = video.isMetaBased

  return (
    <div
      onClick={() => onOpen(video)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#1c1d27",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: "16px",
        overflow: "hidden",
        cursor: "pointer",
        transform: hovered ? "translateY(-3px)" : "none",
        boxShadow: hovered ? "0 12px 28px rgba(0,0,0,0.35)" : "none",
        transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
        borderColor: hovered ? "rgba(246,37,90,0.35)" : "rgba(255,255,255,0.06)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Thumbnail - Instagram reels and TikTok videos are both vertical
          (9:16) source video, so this renders a tall portrait tile instead
          of YouTube's 16:9 one; see `.ugc-video-grid--reels` for the
          matching narrower grid columns. */}
      <div style={{ position: "relative", width: "100%", paddingTop: isPortrait ? "177.78%" : "56.25%", background: "#000" }}>
        <Thumbnail
          src={video.thumbnail_url}
          alt={video.title}
          isInstagram={isInstagram}
          loading="lazy"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
        <div
          style={{
            position: "absolute", inset: 0,
            background: hovered ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0)",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "background 0.18s ease",
          }}
        >
          {hovered && <PlayCircle size={40} color="#fff" style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.5))" }} />}
        </div>

        {video.duration_text && (
          <span style={{
            position: "absolute", bottom: "8px", right: "8px",
            background: "rgba(0,0,0,0.8)", color: "#fff", fontSize: "11px", fontWeight: 600,
            padding: "2px 6px", borderRadius: "5px", fontFamily: "Rubik",
          }}>
            {video.duration_text}
          </span>
        )}

        {/* Reels/TikTok videos open a "watch on <platform>" link right from
            the card, not just from the detail view - `video.url` is the
            real instagram.com/reel/<shortcode>/ or tiktok.com permalink
            (INSTAGRAM_REEL_API.md §5 / TIKTOK_VIDEO_API.md §5), never the
            thumbnail/video file URLs. stopPropagation so this opens the
            platform in a new tab instead of also opening the internal
            detail view. */}
        {isPortrait && video.url && (
          <a
            href={video.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation()
              trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_OUTBOUND_CLICKED, {
                video_id: video.video_id,
                platform: video.platform,
                link_type: "watch",
              })
            }}
            title={`Watch this ${isInstagram ? "reel" : "video"} on ${isInstagram ? "Instagram" : "TikTok"}`}
            style={{
              position: "absolute", bottom: "8px", left: "8px",
              width: "26px", height: "26px", borderRadius: "50%",
              background: "rgba(0,0,0,0.8)", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <ExternalLink size={13} />
          </a>
        )}

        <div style={{ position: "absolute", top: "8px", left: "8px", display: "flex", gap: "6px" }}>
          {video.is_live && (
            <span style={{ display: "flex", alignItems: "center", gap: "4px", background: "#ef4444", color: "#fff", fontSize: "10px", fontWeight: 700, padding: "2px 7px", borderRadius: "5px" }}>
              <Radio size={10} /> LIVE
            </span>
          )}
          {video.is_short && (
            <span style={{ display: "flex", alignItems: "center", gap: "4px", background: "#8b5cf6", color: "#fff", fontSize: "10px", fontWeight: 700, padding: "2px 7px", borderRadius: "5px" }}>
              <Film size={10} /> SHORTS
            </span>
          )}
          {video.is_reel && (
            <span style={{ display: "flex", alignItems: "center", gap: "4px", background: "#E1306C", color: "#fff", fontSize: "10px", fontWeight: 700, padding: "2px 7px", borderRadius: "5px" }}>
              <Film size={10} /> REEL
            </span>
          )}
          {video.is_tiktok && (
            <span style={{ display: "flex", alignItems: "center", gap: "4px", background: "#000", color: "#25F4EE", fontSize: "10px", fontWeight: 700, padding: "2px 7px", borderRadius: "5px" }}>
              <Film size={10} /> TIKTOK
            </span>
          )}
        </div>

        {video.verification?.verified && (
          <span
            title={`Verified about this app - ${Math.round((video.verification.confidence || 0) * 100)}% confidence`}
            style={{
              position: "absolute", top: "8px", right: "8px",
              background: "rgba(16,185,129,0.9)", color: "#fff",
              display: "flex", alignItems: "center", gap: "4px",
              fontSize: "10px", fontWeight: 700, padding: "2px 7px", borderRadius: "5px",
            }}
          >
            <ShieldCheck size={11} /> Verified
          </span>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: "14px 16px 16px", display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
        <h5
          className="ugc-clamp-2"
          style={{ color: "#fff", fontSize: "14.5px", fontWeight: 600, margin: 0, lineHeight: "1.4", fontFamily: "Rubik" }}
          title={video.title}
        >
          {video.title}
        </h5>

        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#9ca3af", flexWrap: "wrap" }}>
          <Avatar src={video.profile_pic_url} alt={video.channel_name} size={16} />
          <a
            href={video.channel_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation()
              trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_OUTBOUND_CLICKED, {
                video_id: video.video_id,
                platform: video.platform,
                link_type: "channel",
              })
            }}
            style={{ color: "#9ca3af", textDecoration: "none", fontWeight: 500 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
          >
            {video.channel_name}
          </a>
          {video.account_verified && (
            <BadgeCheck size={13} color="#3b82f6" title="Verified Instagram account" />
          )}
          <span>•</span>
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Eye size={12} /> {formatViews(video.metrics?.views)}
          </span>
          <span>•</span>
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Calendar size={12} /> {video.published_time_text}
          </span>
          {video.signals?.views_per_day != null && (
            <>
              <span>•</span>
              <span
                title={isInstagram || isTikTok ? `${isInstagram ? "Plays" : "Views"} per day of age.` : "Views per day of age - the only interaction signal available (likes/comments aren't reported by the source)."}
                style={{ display: "flex", alignItems: "center", gap: "4px" }}
              >
                <TrendingUp size={12} /> {formatViews(video.signals.views_per_day)}/day
              </span>
            </>
          )}
        </div>

        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          <Chip label={contentType.label} color={contentType.color} />
          <Chip label={sentiment.label} color={sentiment.color} />
          {isMetaBased && (
            <span
              title={
                isInstagram
                  ? "No caption was written for this reel - the summary was inferred from its hashtags, account and audio rather than what's actually said."
                  : isTikTok
                    ? "No caption was written for this video - the summary was inferred from its hashtags, account and sound rather than what's actually said."
                    : "No captions were available for this video - the summary was inferred from its title and description rather than what's actually said."
              }
              style={{ display: "flex", alignItems: "center", gap: "3px", color: "#6b7280", fontSize: "11px" }}
            >
              <Sparkles size={11} /> AI-inferred
            </span>
          )}
        </div>

        {isPortrait && Array.isArray(video.hashtags) && video.hashtags.length > 0 && (
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {video.hashtags.slice(0, 3).map((tag, i) => (
              <span key={i} style={{ color: "#8b9bd1", fontSize: "11.5px", fontWeight: 500 }}>#{tag}</span>
            ))}
            {video.hashtags.length > 3 && (
              <span style={{ color: "#6b7280", fontSize: "11.5px" }}>+{video.hashtags.length - 3} more</span>
            )}
          </div>
        )}

        {(video.metrics?.likes != null || video.metrics?.comments != null || video.metrics?.shares != null || video.signals?.engagement_rate != null) && (
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", fontSize: "12px", color: "#9ca3af" }}>
            {video.metrics?.likes != null && (
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Heart size={12} /> {formatViews(video.metrics.likes)}</span>
            )}
            {video.metrics?.comments != null && (
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><MessageCircle size={12} /> {formatViews(video.metrics.comments)}</span>
            )}
            {video.metrics?.shares != null && (
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}><Share2 size={12} /> {formatViews(video.metrics.shares)}</span>
            )}
            {/* `saves` (TikTok-only) used to render here as a Bookmark stat.
                Dropped per request: likes is the more familiar, immediately-
                understood number for readers of this card, and it's already
                the first stat shown above whenever the source reports it -
                this just stops "saves" (a more niche metric) from competing
                for the same row. Note "likes" can still legitimately be
                absent on a given video: TikTok's search API sometimes omits
                it per-video (TIKTOK_VIDEO_API.md §"metrics") - a null there
                isn't a bug, it's what the source actually returned. */}
            {video.signals?.engagement_rate != null && (
              <span title={isTikTok ? "(likes + comments + shares + saves) / views" : `(likes + comments + shares) / ${isInstagram ? "plays" : "views"}`} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Percent size={12} /> {(video.signals.engagement_rate * 100).toFixed(1)}%
              </span>
            )}
          </div>
        )}

        {isTikTok && video.music?.title && (
          <div title={video.music.author ? `${video.music.title} — ${video.music.author}` : video.music.title} style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11.5px", color: "#9ca3af" }}>
            <Music size={11} style={{ flexShrink: 0 }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {video.music.title}{video.music.author ? ` — ${video.music.author}` : ""}
            </span>
          </div>
        )}

        {video.signals && (
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", fontSize: "11px", color: "#6b7280" }}>
            {video.signals.creator && (
              isTikTok ? (
                // TikTok's tier is a **real follower band**, not observed
                // reach - `basis` is always "author_followers"
                // (TIKTOK_VIDEO_API.md §5). `views_per_follower` says whether
                // this video escaped the creator's own audience.
                <span
                  title={`Real follower band (not reach) - ${formatViews(video.signals.creator.followers)} followers${video.signals.creator.views_per_follower != null ? `, ${video.signals.creator.views_per_follower.toFixed(1)}x views per follower` : ""}.`}
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <Users size={11} />
                  {CREATOR_TIER_LABEL_TIKTOK[video.signals.creator.tier] || CREATOR_TIER_LABEL_TIKTOK.unknown}
                  {video.signals.creator.followers != null ? ` (${formatViews(video.signals.creator.followers)})` : ""}
                </span>
              ) : (
                <span
                  title={video.signals.creator.videos_in_store === 1
                    ? `Only one ${isInstagram ? "reel" : "video"} from this ${isInstagram ? "account" : "channel"} is tracked yet - too little to grade its reach.`
                    : `Views-based reach tier (not ${isInstagram ? "followers" : "subscribers"}) - median ${formatViews(video.signals.creator.median_views)} views across ${video.signals.creator.videos_in_store} tracked ${isInstagram ? "reels" : "videos"}.`}
                  style={{ display: "flex", alignItems: "center", gap: "4px" }}
                >
                  <Users size={11} />
                  {video.signals.creator.videos_in_store === 1
                    ? `${formatViews(video.signals.creator.median_views)} views (1 ${isInstagram ? "reel" : "video"} tracked)`
                    : CREATOR_TIER_LABEL[video.signals.creator.tier] || CREATOR_TIER_LABEL.unknown}
                </span>
              )
            )}
            {video.signals.growth?.measured && (
              <span
                title={`+${formatViews(video.signals.growth.views_gained)} views over ${video.signals.growth.over_days} day${video.signals.growth.over_days === 1 ? "" : "s"}`}
                style={{ display: "flex", alignItems: "center", gap: "4px", color: "#10b981" }}
              >
                <TrendingUp size={11} /> +{formatViews(video.signals.growth.views_gained)} views
              </span>
            )}
          </div>
        )}

        {video.summary?.summary && (
          <p className="ugc-clamp-3" style={{ color: "#adb5bd", fontSize: "12.5px", lineHeight: "1.5", margin: "2px 0 0" }}>
            {video.summary.summary}
          </p>
        )}
      </div>
    </div>
  )
}

export default VideoCard
